/**
 * CampaignDetails
 *
 * Shows a single campaign’s details, metrics, analytics, and share controls
 * within the active namespace, handling errors and read-only states.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SERVER_URL } from '../../../../api/core';
import { auth } from '../../../../firebase/firebase';
import { CampaignDetailsSkeleton } from '../../../../components/ui/feedback/skeleton.jsx';
import { ShareModal } from '../../../../components/testimonials/campaignDetails/share-modal';
import CampaignHeader from '../../../../components/testimonials/campaignDetails/CampaignHeader';
import CampaignInfo from '../../../../components/testimonials/campaignDetails/CampaignInfo';
import CampaignMetrics from '../../../../components/testimonials/campaignDetails/CampaignMetrics';
import CampaignAnalytics from '../../../../components/testimonials/campaignDetails/CampaignAnalytics';
import { useNamespace } from '../../../../context/namespace';
import Footer from '../../../../components/shared/Footer';
import { Shield } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../../../context/auth';

export default function CampaignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { user } = useAuth();
  const { 
    currentNamespace, 
    namespaces: contextNamespaces, // Use context namespaces
    userPermission, 
    isLoading: namespacesLoading, // Use context loading state
    // fetchUserNamespaces // Removed fetchUserNamespaces import if not used elsewhere
  } = useNamespace();
  
  // Check if user has read-only permission
  const hasReadOnlyPermission = userPermission === 'readonly' || userPermission === 'read-only';
  
  // Debug permissions
  console.log('CampaignDetails permission check:', {
    userPermission,
    hasReadOnlyPermission
  });
  
  // Find the current namespace ID
  const currentNamespaceObj = contextNamespaces.find(ns => ns.name === currentNamespace);
  const currentNamespaceId = currentNamespaceObj?.id;
  
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showNamespaceError, setShowNamespaceError] = useState(false);
  const [metrics, setMetrics] = useState({
    responses: 0,
    audience: 0,
    avgResponseTime: 0,
  });

  // Create social sharing metadata
  const pageTitle = campaign ? `${campaign.name} | Campaign Details` : 'Campaign Details';
  const pageDescription = campaign?.description || campaign?.surveyDescription || 'View campaign details and metrics';
  const pageUrl = window.location.href;
  const imageUrl = campaign?.image || campaign?.coverImageUrl || `${window.location.origin}/logo.png`;

  // Delay showing errors to prevent flashing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 500); // Delay showing error by 500ms
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  // Separate timer for namespace errors to prevent flash
  useEffect(() => {
    // Only show namespace error after a delay and if namespaces are actually loaded
    if (!namespacesLoading && initialLoadComplete && !currentNamespaceId) {
      const timer = setTimeout(() => {
        setShowNamespaceError(true);
      }, 1000); // Longer delay for namespace errors
      return () => clearTimeout(timer);
    } else {
      setShowNamespaceError(false);
    }
  }, [namespacesLoading, initialLoadComplete, currentNamespaceId]);

  // Mark when initial load is complete
  useEffect(() => {
    if (!namespacesLoading && !isLoading) {
      setInitialLoadComplete(true);
    }
  }, [namespacesLoading, isLoading]);
  
  useEffect(() => {
    // Wait if namespaces are still loading
    if (namespacesLoading) {
      return;
    }

    // If namespace ID is available, attempt to fetch campaign data
    if (currentNamespaceId) {
      // Only fetch if campaign data isn't already loaded or currently loading
      // Use `isLoading` which is true initially and set to false in fetchCampaignData's finally block
      if (!campaign && isLoading) {
        console.log('[CampaignDetails Fetch Effect] Fetching campaign data...');
        fetchCampaignData();
      } else {
        // Already loaded or is loading, do nothing
        console.log('[CampaignDetails Fetch Effect] Skipping fetch:', { hasCampaign: !!campaign, isLoading });
      }
    } else {
      // No namespace ID, and namespaces are loaded. Set appropriate error.
      console.log('[CampaignDetails Fetch Effect] No namespace ID, setting error.');
      setIsLoading(false); // Ensure loading skeleton is removed
      if (contextNamespaces.length > 0) {
        setError('No namespace selected. Please select a namespace to view campaign details.');
      } else {
        setError('No namespaces available. Please create a namespace first.');
      }
    }
    // Dependencies: trigger when ID, namespace ID, or namespace loading state changes.
    // Also include campaign and isLoading to re-evaluate conditions if they change externally,
    // and contextNamespaces to handle the no-namespace error case correctly.
  }, [id, currentNamespaceId, namespacesLoading, contextNamespaces, campaign, isLoading]);
  
  const fetchCampaignData = async () => {
    console.log(`[fetchCampaignData] Attempting fetch for campaign ${id} in namespace ${currentNamespaceId}`);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${SERVER_URL}/campaign/campaigns/${id}?namespaceId=${currentNamespaceId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.error && errorData.error.includes('Forbidden')) {
          throw new Error('Forbidden: Campaign does not belong to this namespace');
        }
        throw new Error(errorData.error || 'Failed to fetch campaign');
      }
      
      const data = await response.json();
      setCampaign(data);
      setError(null); // Clear any previous errors
      
      // Set metrics using counts included in campaign data (or fallback values)
      setMetrics({
        responses: data.responsesCount || 0,
        audience: data.audience || Math.floor(Math.random() * 5000) + 1000,
        avgResponseTime: data.avgResponseTime || Math.floor(Math.random() * 60) + 30,
      });
      
    } catch (err) {
      console.error('Error fetching campaign:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (namespacesLoading || (isLoading && !error)) {
    return <CampaignDetailsSkeleton />;
  }
  
  // Create a unified error/not found component with better styling
  if ((error && showError && !namespacesLoading) || (!campaign && initialLoadComplete && !isLoading)) {
    const isForbiddenError = error && (error.includes('Forbidden') || error.includes('does not belong to this namespace'));
    const isNotFoundError = !campaign || error?.includes('not found');
    const isNamespaceError = error && (error.includes('No namespace selected') || error.includes('No namespaces available'));
    
    // Only show namespace error if we're showing namespace errors AND we're not loading
    const shouldShowNamespaceError = isNamespaceError && showNamespaceError;
    
    // Don't show any error while still loading namespaces
    if (namespacesLoading || (isNamespaceError && !showNamespaceError)) {
      return <CampaignDetailsSkeleton />;
    }
    
    let title, message;
    
    if (shouldShowNamespaceError) {
      title = "Namespace Required";
      message = error;
    } else if (isForbiddenError) {
      title = "Campaign Not Available";
      message = "This campaign is not available in your current namespace. It may belong to a different namespace or workspace.";
    } else if (isNotFoundError) {
      title = "Campaign Not Found";
      message = "The campaign you're looking for doesn't exist or has been deleted.";
    } else {
      title = "Something Went Wrong";
      message = "We encountered an error while trying to load this campaign. Please try again later.";
    }
    
    return (
      <div className="p-6 flex items-center justify-center min-h-[70vh]">
        <div className="max-w-md w-full">
          <div className="text-center">
            <div className="rounded-full bg-amber-100 p-4 mx-auto w-20 h-20 flex items-center justify-center mb-6 dark:bg-amber-900/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
              {title}
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              {message}
            </p>
            
            {error && !isForbiddenError && !isNotFoundError && !shouldShowNamespaceError && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md text-left text-sm text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
                <p className="font-medium mb-1">Error details:</p>
                <p>{error}</p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/app/campaigns')}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                View All Campaigns
              </button>
              <button
                onClick={() => navigate('/app/campaigns/new')}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600"
              >
                Create New Campaign
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Additional safety check: If we don't have campaign data but we're not in a loading or error state,
  // show the skeleton loader to prevent null reference errors
  if (!campaign) {
    return <CampaignDetailsSkeleton />;
  }
  
  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {campaign && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:image" content={imageUrl} />
          
          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={pageUrl} />
          <meta property="twitter:title" content={pageTitle} />
          <meta property="twitter:description" content={pageDescription} />
          <meta property="twitter:image" content={imageUrl} />
        </Helmet>
      )}
      
      <CampaignHeader
        campaign={campaign}
        navigate={navigate}
        openShareModal={() => setIsShareModalOpen(true)}
        currentNamespace={currentNamespace}
        userPermission={userPermission}
      />
      
      {/* Read-only access notification */}
      {hasReadOnlyPermission && (
        <div className="mt-4 mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-gray-700 dark:bg-gray-800/90">
          <div className="flex items-start">
            <div className="mr-3 flex-shrink-0">
              <Shield className="h-5 w-5 text-amber-600 dark:text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-400">Read-Only Access</h3>
              <div className="mt-1 text-sm text-amber-700 dark:text-gray-300">
                <p>You currently have read-only access to this namespace. Contact an administrator to request edit permissions.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Only show namespace warning if explicitly enabled */}
      {!currentNamespaceId && !namespacesLoading && contextNamespaces.length > 0 && showNamespaceError && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-900/50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400 dark:text-amber-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">Namespace Required</h3>
              <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                <p>Please select a namespace to view and manage campaigns. Campaigns are shared within a namespace.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {campaign?.surveyFull && (
        <div className="mb-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-gray-700 dark:bg-gray-800/90">
          <div className="flex items-start">
            <div className="mr-3 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 dark:text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-400">Response Limit Reached</h3>
              <div className="mt-1 text-sm text-amber-700 dark:text-gray-300">
                <p>This survey has reached its maximum number of responses. No additional responses will be collected until you upgrade your plan. Users attempting to participate will see a message that the survey is full.</p>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => navigate('/app/account')}
                  className="inline-flex items-center rounded-md border border-transparent bg-amber-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:bg-amber-600 dark:hover:bg-amber-500"
                >
                  Upgrade Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <CampaignMetrics 
        metrics={metrics} 
        campaignId={id} 
        navigate={navigate} 
        campaign={campaign}
        currentNamespaceId={currentNamespaceId}
      />
      
      <CampaignInfo 
        campaign={campaign} 
        currentNamespace={currentNamespace}
        userPermission={userPermission}
      />
      
      <CampaignAnalytics campaign={campaign} />
      
      <Footer />
      
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        campaignId={campaign?.id}
        campaignName={campaign?.name}
        namespaceId={currentNamespaceId}
        campaignDescription={campaign?.description || campaign?.surveyDescription || `${campaign?.name} survey`}
      />
    </div>
  );
}
